import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  FormControl,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyContact = {
  contactId: "",
  storeId: "",
  contactType: "",
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  status: "",
};

const contactStatusOptions = [
  "Active",
  "Inactive",
  "Prospect",
  "Customer",
  "Vendor",
];
const contactTypeOptions = [
  "Individual",
  "Company",
  "Supplier",
  "Partner",
  "Other",
];

export default class CrmContacts extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      contacts: [],
      filteredContacts: [],
      loading: true,
      search: "",
      statusFilter: "",
      typeFilter: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentContact: { ...emptyContact },
    };
  }

  componentDidMount() {
    this.loadContacts();
  }

  loadContacts = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const contacts = await fetchCrmList("Contacts", "GetContacts", storeId);
      const normalized = Array.isArray(contacts) ? contacts : [];
      this.setState({
        contacts: normalized,
        filteredContacts: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load contacts.",
      });
    }
  };

  filterContacts = ({ search, statusFilter, typeFilter }) => {
    const normalizedSearch = (search || "").trim().toLowerCase();

    return this.state.contacts.filter((contact) => {
      const name =
        `${contact.firstName || ""} ${contact.lastName || ""}`.toLowerCase();
      const email = (contact.email || "").toLowerCase();
      const phone = (contact.phone || "").toLowerCase();
      const company = (contact.companyName || "").toLowerCase();
      const location =
        `${contact.city || ""} ${contact.state || ""} ${contact.country || ""}`.toLowerCase();
      const status = (contact.status || "").toLowerCase();
      const type = (contact.contactType || "").toLowerCase();

      const searchMatch =
        name.includes(normalizedSearch) ||
        email.includes(normalizedSearch) ||
        phone.includes(normalizedSearch) ||
        company.includes(normalizedSearch) ||
        location.includes(normalizedSearch);

      const statusMatch =
        !statusFilter || status === statusFilter.toLowerCase();
      const typeMatch = !typeFilter || type === typeFilter.toLowerCase();

      return searchMatch && statusMatch && typeMatch;
    });
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const { statusFilter, typeFilter } = this.state;
    this.setState({
      search,
      filteredContacts: this.filterContacts({
        search,
        statusFilter,
        typeFilter,
      }),
    });
  };

  onStatusFilterChange = (event) => {
    const statusFilter = event.target.value;
    this.setState({
      statusFilter,
      filteredContacts: this.filterContacts({
        search: this.state.search,
        statusFilter,
        typeFilter: this.state.typeFilter,
      }),
    });
  };

  onTypeFilterChange = (event) => {
    const typeFilter = event.target.value;
    this.setState({
      typeFilter,
      filteredContacts: this.filterContacts({
        search: this.state.search,
        statusFilter: this.state.statusFilter,
        typeFilter,
      }),
    });
  };

  formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  handleShowModal = (contact = null) => {
    this.setState({
      currentContact: contact ? { ...contact } : { ...emptyContact },
      isEdit: Boolean(contact),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentContact: {
        ...prevState.currentContact,
        [field]: value,
      },
    }));
  };

  saveContact = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const contact = {
      ...this.state.currentContact,
      storeId,
      contactId:
        this.state.currentContact.contactId ||
        "00000000-0000-0000-0000-000000000000",
    };

    try {
      await saveCrmItem("Contacts", "AddOrUpdateContact", contact);
      await this.loadContacts();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save contact." });
    }
  };

  deleteContact = async (contactId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;

    if (!window.confirm("Delete this contact?")) {
      return;
    }

    try {
      await deleteCrmItem("Contacts", "DeleteContact", storeId, contactId);
      await this.loadContacts();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete contact." });
    }
  };

  render() {
    const {
      filteredContacts,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentContact,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Contacts</h2>
            <p className="text-muted">
              Manage contacts for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Contact
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadContacts}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM contacts.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={4} lg={3}>
            <FormControl
              placeholder="Search by name, email, phone, company or location"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
          <Col md={3} lg={2}>
            <Form.Select
              value={this.state.typeFilter}
              onChange={this.onTypeFilterChange}
            >
              <option value="">All types</option>
              {contactTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3} lg={2}>
            <Form.Select
              value={this.state.statusFilter}
              onChange={this.onStatusFilterChange}
            >
              <option value="">All statuses</option>
              {contactStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2} lg={3} className="text-end align-self-center">
            <small className="text-muted">
              Showing {filteredContacts.length} of {this.state.contacts.length}
            </small>
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Company</th>
              <th>Location</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredContacts.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  No contacts found.
                </td>
              </tr>
            ) : (
              filteredContacts.map((contact) => (
                <tr key={contact.contactId || contact.email || Math.random()}>
                  <td>
                    {`${contact.firstName || ""} ${contact.lastName || ""}`.trim()}
                  </td>
                  <td>{contact.contactType || "-"}</td>
                  <td>
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{contact.companyName || "-"}</td>
                  <td>
                    {`${contact.city || ""}${contact.city ? ", " : ""}${contact.state || ""}${contact.state ? ", " : ""}${contact.country || ""}` ||
                      "-"}
                  </td>
                  <td>{contact.status || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(contact)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteContact(contact.contactId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit Contact" : "Add Contact"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="contactFirstName">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      value={currentContact.firstName}
                      onChange={(e) =>
                        this.handleFieldChange("firstName", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactLastName">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      value={currentContact.lastName}
                      onChange={(e) =>
                        this.handleFieldChange("lastName", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={currentContact.email}
                      onChange={(e) =>
                        this.handleFieldChange("email", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactPhone">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      value={currentContact.phone}
                      onChange={(e) =>
                        this.handleFieldChange("phone", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactCompanyName">
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      value={currentContact.companyName}
                      onChange={(e) =>
                        this.handleFieldChange("companyName", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={currentContact.status}
                      onChange={(e) =>
                        this.handleFieldChange("status", e.target.value)
                      }
                    >
                      <option value="">Choose status</option>
                      {contactStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactType">
                    <Form.Label>Contact Type</Form.Label>
                    <Form.Select
                      value={currentContact.contactType}
                      onChange={(e) =>
                        this.handleFieldChange("contactType", e.target.value)
                      }
                    >
                      <option value="">Choose type</option>
                      {contactTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactCountry">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      value={currentContact.country}
                      onChange={(e) =>
                        this.handleFieldChange("country", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactState">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      value={currentContact.state}
                      onChange={(e) =>
                        this.handleFieldChange("state", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactCity">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      value={currentContact.city}
                      onChange={(e) =>
                        this.handleFieldChange("city", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactPostalCode">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      value={currentContact.postalCode}
                      onChange={(e) =>
                        this.handleFieldChange("postalCode", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="contactAddress">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={currentContact.address}
                      onChange={(e) =>
                        this.handleFieldChange("address", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveContact}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
